import { ButtonPrimeReact, Tabs } from "@finanedi.es/storybook"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useQuery } from "@tanstack/react-query"
import { ConceptoGetTicketsByIdDto } from "api/DTOs/ConceptoGetTicketsByIdDto"
import { getFicheroIdFile } from "api/Fichero.service"
import { ticketById } from "api/Ticket.service"
import { DATE_DD_MM_YYYY } from "common/components/FilterMenu/constants"
import Modal from "common/components/Modal/Modal"
import NativePDFViewer from "common/components/NativePDF/NativePDF"
import Spinner from "common/components/Spinner"
import { formatNumber } from "common/utils/currency"
import { formatDate } from "common/utils/date"
import ModalAsientoContable from "features/Contabilidad/components/ModalAsientoContable/ModalAsientoContable"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"

const ModalDetailTicket = ({
  showModal,
  onHide,
  data,
  onDelete,
}: {
  showModal: boolean
  onHide: () => void
  data: string | null
  onDelete: () => void
}) => {
  const history = useHistory()
  const { t } = useTranslation()
  const [openModalAsiento, setOpenModalAsiento] = useState<boolean>(false)
  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 1023px)").matches
  )

  const {
    isLoading: isLoadingTicket,
    data: ticketData,
    refetch: refetchTicket,
  } = useQuery({
    queryKey: ["ticketById", data],
    queryFn: async () => {
      if (!data) return

      const ticket = await ticketById(data)
      if (!ticket) {
        throw new Error("No se pudo obtener el ticket")
      }

      return ticket
    },
    enabled: !!data,
  })

  const { isLoading: loadingFile, data: filePdf } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["ticketFile", ticketData?.ficheroId],
    queryFn: async () => {
      if (!ticketData?.ficheroId) return
      const newFichero = await getFicheroIdFile(ticketData.ficheroId)
      return newFichero
    },
    enabled: !!ticketData?.ficheroId,
  })

  const handleEditTicket = (): void => {
    ticketData &&
      history.push(`/documentos/tickets/${ticketData?.id}`, {
        ticket: { ...ticketData, fichero: filePdf },
      })
  }

  const headerElement = (): JSX.Element => (
    <div className='tw-w-full !tw-p-0 !tw-h-10 tw-flex tw-justify-between tw-items-center tw-flex-row'>
      <span className='tw-text-2xl tw-font-medium !tw-p-0'>
        {t("tickets.detail.title")}
      </span>
      <div className='tw-flex tw-flex-row !tw-p-0 tw-gap-4'>
        <div
          className='tw-hidden tw-h-10 tw-w-10 lg:tw-flex tw-justify-center tw-items-center !tw-p-0 tw-cursor-pointer tw-shadow-deleteBtn'
          onClick={onDelete}
        >
          <FontAwesomeIcon
            icon={["fal", "trash-alt"]}
            className='tw-h-5 tw-w-5 tw-text-danger'
          />
        </div>
        <ButtonPrimeReact
          className='!tw-hidden lg:!tw-block !tw-px-7 !tw-border-none !tw-bg-primary'
          onClick={handleEditTicket}
        >
          {t("tickets.detail.editText")}
        </ButtonPrimeReact>
        <div
          className='tw-h-10 tw-w-10 tw-flex tw-justify-center tw-items-center !tw-p-0 tw-m-0 tw-cursor-pointer'
          onClick={onHide}
        >
          <FontAwesomeIcon
            icon={["fal", "x"]}
            className='tw-h-5 tw-w-5'
          />
        </div>
      </div>
    </div>
  )

  const TabImg = () => {
    return (
      <div className='tw-flex tw-gap-2 tw-items-center tw-h-full'>
        {!loadingFile ? (
          <>
            <NativePDFViewer
              pdf={filePdf}
              type='stream'
            />
          </>
        ) : (
          <div className='tw-w-full tw-h-full tw-flex tw-justify-center tw-items-center'>
            <Spinner className='tw-text-secondary' />
          </div>
        )}
      </div>
    )
  }
  const TicketsDetail = () => {
    return (
      <div className='tw-grid tw-grid-cols-10 tw-bg-simple-10 tw-gap-4 tw-pt-2 lg:!tw-h-full'>
        <div className='tw-w-full tw-col-span-10 lg:tw-col-span-4 tw-h-fit '>
          <div className='tw-w-full tw-h-fit tw-p-1 tw-bg-background tw-rounded'>
            <div className='tw-flex tw-w-full tw-h-fit tw-bg-simple-10 tw-rounded tw-p-4'>
              <div className='tw-flex tw-flex-col tw-w-full'>
                <div className='tw-w-full tw-flex tw-flex-row tw-justify-between tw-mb-4'>
                  <span>
                    {isMobile
                      ? t("tickets.detail.description")
                      : t("tickets.detail.nombre")}
                  </span>
                  <span className='tw-font-medium tw-text-right tw-max-w-48 mobile:tw-max-w-fit lg:tw-max-w-56'>
                    {ticketData?.nombre ?? "-"}
                  </span>
                </div>
                <div className='lg:tw-hidden tw-w-full tw-flex tw-flex-row tw-justify-between tw-mb-4'>
                  <span>{t("tickets.detail.category")}</span>
                  <span className='tw-font-medium'>
                    {ticketData?.categoria?.nombre ?? "-"}
                  </span>
                </div>
                <div className='tw-w-full tw-flex tw-flex-row tw-justify-between tw-mb-4'>
                  <span>{t("tickets.detail.fechaEmision")}</span>
                  <span className='tw-font-medium'>
                    {formatDate(ticketData?.fechaEmision, DATE_DD_MM_YYYY)}
                  </span>
                </div>
                <div className='tw-w-full tw-flex tw-flex-row tw-justify-between tw-mb-4'>
                  <span>{t("tickets.detail.importe")}</span>
                  <span className='tw-font-medium'>
                    {ticketData?.conceptos
                      ? formatNumber(
                          ticketData?.conceptos?.reduce(
                            (
                              acc: number,
                              elemento: ConceptoGetTicketsByIdDto
                            ) => acc + elemento.cantidad * elemento.precio,
                            0
                          )
                        )
                      : 0}{" "}
                    €
                  </span>
                </div>
                <div className='tw-w-full tw-flex tw-flex-row tw-justify-between tw-mb-4'>
                  <span>{t("tickets.detail.numero")}</span>
                  <span className='tw-font-medium'>{ticketData?.numero}</span>
                </div>
                <div className='tw-w-full tw-flex tw-flex-row tw-justify-between'>
                  <span>{t("tickets.detail.contacto")}</span>
                  <span className='tw-font-medium tw-text-right tw-max-w-48 mobile:tw-max-w-fit lg:tw-max-w-56'>
                    {ticketData?.empresa?.razonSocial ?? "-"}
                  </span>
                </div>
              </div>
            </div>
          </div>
          {ticketData?.asientoContable && !isMobile && (
            <div className='tw-flex tw-w-full tw-h-fit tw-bg-simple-10 tw-rounded tw-p-4'>
              <div className='tw-flex tw-flex-col tw-w-full'>
                <span className='tw-font-medium tw-text-base'>
                  {t("tickets.detail.accounting")}
                </span>
                <div className='tw-flex tw-flex-row tw-justify-between tw-items-center tw-mt-3'>
                  <div className='tw-flex tw-flex-row'>
                    <span className='tw-text-nowrap tw-text-base'>
                      {t("tickets.detail.siteAccounting")}:
                    </span>
                    <span className='tw-font-medium tw-text-base tw-ml-1'>
                      {ticketData?.asientoContable?.numero}
                    </span>
                  </div>
                  <span
                    className='tw-text-nowrap tw-text-info-100 tw-text-base tw-cursor-pointer hover:tw-underline'
                    onClick={() => setOpenModalAsiento(true)}
                  >
                    {t("tickets.detail.showSiteAccounting")}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className='tw-hidden lg:tw-flex !tw-h-full tw-col-span-6 tw-justify-center tw-w-full tw-bg-simple-10 tw-shadow tw-rounded-lg'>
          {!loadingFile ? (
            <div className='tw-w-full tw-h-full tw-p-4'>
              <NativePDFViewer
                pdf={filePdf}
                type='stream'
              />
            </div>
          ) : (
            <div className='tw-w-full tw-h-full tw-flex tw-justify-center tw-items-center'>
              <Spinner className='tw-text-secondary' />
            </div>
          )}
        </div>
      </div>
    )
  }

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.matchMedia("(max-width: 1023px)").matches)
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile])

  return (
    <Modal
      className='!tw-h-5/6'
      headerClassName='tw-border-none !tw-bg-primary tw-w-full tw-flex tw-items-center'
      header={headerElement}
      size={isMobile ? "normal" : "extra-big"}
      maskClassName='!tw-z-20'
      pt={{
        root: {
          className: "!tw-h-3/4",
        },
      }}
      visible={showModal}
      onHide={onHide}
      closable={false}
      draggable
      keepInViewport={false}
    >
      {isLoadingTicket ? (
        <div className='tw-flex tw-justify-center tw-items-center tw-font- tw-text- tw-w-full !tw-h-full tw-pt-2 tw-grid-col'>
          <Spinner className='tw-text-secondary' />
        </div>
      ) : isMobile ? (
        <Tabs
          tabsContainerTailwind='text-gray-500 tw-grid tw-grid-flow-col tw-grid-cols-2 tw-rounded tw-text-md tw-font-base tw-bg-gray-100 tw-p-1 tw-text-center tw-mb-4 tw-text-sm tw-font-medium'
          tabsTailwind='tw-select-none tw-flex tw-justify-center tw-items-center tw-text-neutral-75 tw-rounded tw-py-3 tw-px-10 tw-cursor-pointer hover:tw-text-black hover:tw-shadow tw-transition-shadow tw-duration-150 tw-ease-in-out'
          tabsActiveTailwind='tw-bg-white tw-text-primary tw-shadow tw-flex tw-items-center tw-justify-center tw-rounded'
          tabTitle={[]}
          tabs={[
            {
              element: <span key='Details'>{t("tickets.detail.details")}</span>,
              disabled: false,
            },
            {
              element: <span key='Img'>{t("tickets.detail.image")}</span>,
              disabled: false,
            },
          ]}
          tabChild={[<TicketsDetail key='Details' />, <TabImg key='Img' />]}
        />
      ) : (
        <TicketsDetail />
      )}

      <ModalAsientoContable
        idToEdit={ticketData?.asientoContable?.id}
        show={openModalAsiento}
        setShow={setOpenModalAsiento}
        updateTable={() => refetchTicket}
      />
    </Modal>
  )
}

export default ModalDetailTicket
